import { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";
import { Tooltip, TextField, MenuItem } from "@mui/material";
import { toast } from "react-toastify";
import getColumns from "./column.js";
import DynamicTable from "../../../components/common/DynamicTable.jsx";
import useModal from "../../../hooks/useModal.js";
import useConfirm from "../../../hooks/useConfirm.js";
import { deleteWageRate, getWageRates } from "../../../services/wageRate.js";
import AddEditWageRate from "./AddEditWageRate.jsx";
import usePaginatedList from "../../../hooks/usePaginatedList.js";

const PAY_BASIS_OPTIONS = [
  { label: "Daily", value: "DAILY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Bi-weekly", value: "BIWEEKLY" },
  { label: "Hourly", value: "HOURLY" },
];

const WageRateList = () => {
  const { confirm } = useConfirm();
  const { showModal } = useModal();
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState({});

  const fetchData = async (params) => {
    return await getWageRates(params.page, params);
  };

  const { data, pagination, setPage, setItemsPerPage, setFilters, refresh } = usePaginatedList(fetchData);

  const handleApplyFilters = () => {
    setFilters({ filters: filterValues });
    setShowFilter(false);
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setFilters({ filters: {} });
    setShowFilter(false);
  };

  const handleCreate = () => {
    showModal("Add Wage Rate", <AddEditWageRate refresh={refresh} />, "xl");
  };

  const handleEdit = (row) => {
    showModal("Edit Wage Rate", <AddEditWageRate refresh={refresh} existingField={row} />, "xl");
  };

  const handleDelete = async (id) => {
    try {
      const isConfirmed = await confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this wage rate?",
        confirmText: "Yes, Delete",
        cancelText: "Cancel",
      });
      if (!isConfirmed) return;

      await deleteWageRate(id);
      toast.success("Wage rate deleted successfully!");
      refresh();
    } catch (error) {
      console.log("Error in deleting wage rate", error);
      toast.error(error?.response?.data?.message || "Error in deleting wage rate");
    }
  };

  return (
    <>
      <div className="bg-light mb-2">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 text-muted small">Show</label>
              <select
                className="form-select form-select-sm"
                style={{ width: "80px" }}
                value={pagination?.itemsPerPage || 20}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-muted small">entries</span>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-success btn-sm d-flex align-items-center" onClick={handleCreate}>
                <AddIcon sx={{ fontSize: 18 }} className="me-1" />
                <span className="d-none d-sm-inline">Add Wage Rate</span>
              </button>

              <Tooltip title="Filter">
                <button className="btn btn-outline-dark btn-sm" onClick={() => setShowFilter(!showFilter)}>
                  <TuneIcon sx={{ fontSize: 18 }} />
                </button>
              </Tooltip>
            </div>
          </div>

          {showFilter && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              <TextField
                select
                size="small"
                label="Pay Basis"
                style={{ minWidth: 180 }}
                value={filterValues.pay_basis || ""}
                onChange={(e) => setFilterValues((prev) => ({ ...prev, pay_basis: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {PAY_BASIS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <button className="btn btn-danger btn-sm" onClick={handleApplyFilters}>
                Apply
              </button>
              <button className="btn btn-secondary btn-sm" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      <DynamicTable
        columns={getColumns(handleEdit, handleDelete)}
        data={data}
        rowKey="id"
        currentPage={pagination?.currentPage}
        totalPages={pagination?.totalPages}
        itemsPerPage={pagination?.itemsPerPage}
        totalItems={pagination?.totalItems}
        onPageChange={(page) => setPage(page)}
      />
    </>
  );
};

export default WageRateList;
